import { CONFERENCE_WILL_JOIN, CONFERENCE_WILL_LEAVE, ENDPOINT_MESSAGE_RECEIVED } from '../base/conference/actionTypes';
import { getCurrentConference } from '../base/conference/functions';
import { SET_SCREENSHARE_MUTED } from '../base/media/actionTypes';
import { VIDEO_TYPE } from '../base/media/constants';
import { getLocalParticipant } from '../base/participants/functions';
import { MiddlewareRegistry } from '../base/redux';
import { TRACK_REMOVED } from '../base/tracks/actionTypes';

import { LOCAL_VIDEO_ZOOM_CHANGED } from './actionTypes';
import { localVideoZoomChanged, remoteVideoZoomChanged } from './actions';
import {
    CAMERA_ZOOM_SYNC_COMMAND,
    SCREENSHARE_ZOOM_SYNC_COMMAND,
    ZOOM_SYNC_EPSILON,
    ZOOM_SYNC_SEND_INTERVAL_MS
} from './constants';
import {
    IDENTITY_ZOOM_STATE,
    getLocalVideoZoom,
    getRemoteVideoZoom,
    isSameZoomState,
    parseZoomCommand
} from './functions';
import logger from './logger';

const COMMAND_BY_VIDEO_TYPE = {
    [VIDEO_TYPE.CAMERA]: CAMERA_ZOOM_SYNC_COMMAND,
    [VIDEO_TYPE.DESKTOP]: SCREENSHARE_ZOOM_SYNC_COMMAND
};

/**
 * Presence handlers registered on the current conference, so they can be
 * removed again.
 */
let _handlers = null;

/**
 * Per video type: the state last written to presence and the throttle timer
 * of a pending write.
 */
const _sending = {
    [VIDEO_TYPE.CAMERA]: {
        sent: undefined,
        timer: null
    },
    [VIDEO_TYPE.DESKTOP]: {
        sent: undefined,
        timer: null
    }
};

/**
 * Forwards the local participant's view zoom of their own video to the other
 * participants and stores the zoom the others report for theirs.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
    case CONFERENCE_WILL_JOIN:
        _listen(store, action.conference);
        break;

    case CONFERENCE_WILL_LEAVE:
        _unlisten(action.conference);
        break;

    case ENDPOINT_MESSAGE_RECEIVED: {
        let { data } = action;

        if (typeof data === 'string') {
            try {
                data = JSON.parse(data);
            } catch {
                // Not JSON, ignore
            }
        }

        if (data?.type === 'MODERATOR_ZOOM_SYNC') {
            const { participantId, videoType, zoomState } = data;

            if (participantId && videoType && zoomState) {
                const parsedState = parseZoomCommand(zoomState);

                if (parsedState && !isSameZoomState(getRemoteVideoZoom(store.getState(), participantId, videoType), parsedState)) {
                    store.dispatch(remoteVideoZoomChanged(participantId, videoType, parsedState));
                }
            }
        }
        break;
    }

    case LOCAL_VIDEO_ZOOM_CHANGED: {
        const result = next(action);

        _scheduleSend(store, action.videoType);

        return result;
    }

    case SET_SCREENSHARE_MUTED: {
        const result = next(action);

        // A stopped share is muted on web rather than removed; the next one
        // must not start where the previous one was zoomed.
        action.muted && _resetLocalZoom(store, VIDEO_TYPE.DESKTOP);

        return result;
    }

    case TRACK_REMOVED: {
        const result = next(action);
        const { mhTrack } = action.track || {};

        if (mhTrack?.isLocal?.() && mhTrack.videoType === VIDEO_TYPE.DESKTOP) {
            _resetLocalZoom(store, VIDEO_TYPE.DESKTOP);
        }

        return result;
    }
    }

    return next(action);
});

/**
 * Registers the presence command listeners on a conference.
 *
 * @param {Store} store - The redux store.
 * @param {Object} conference - The conference being joined.
 * @private
 * @returns {void}
 */
function _listen(store, conference) {
    if (!conference || _handlers) {
        return;
    }

    _handlers = {
        conference,
        [CAMERA_ZOOM_SYNC_COMMAND]: (node, id) => _onCommand(store, VIDEO_TYPE.CAMERA, node, id),
        [SCREENSHARE_ZOOM_SYNC_COMMAND]: (node, id) => _onCommand(store, VIDEO_TYPE.DESKTOP, node, id)
    };

    conference.addCommandListener(CAMERA_ZOOM_SYNC_COMMAND, _handlers[CAMERA_ZOOM_SYNC_COMMAND]);
    conference.addCommandListener(SCREENSHARE_ZOOM_SYNC_COMMAND, _handlers[SCREENSHARE_ZOOM_SYNC_COMMAND]);
}

/**
 * Removes the presence command listeners from a conference.
 *
 * @param {Object} conference - The conference being left.
 * @private
 * @returns {void}
 */
function _unlisten(conference) {
    if (!_handlers || (conference && _handlers.conference !== conference)) {
        return;
    }

    try {
        _handlers.conference.removeCommandListener(
            CAMERA_ZOOM_SYNC_COMMAND, _handlers[CAMERA_ZOOM_SYNC_COMMAND]);
        _handlers.conference.removeCommandListener(
            SCREENSHARE_ZOOM_SYNC_COMMAND, _handlers[SCREENSHARE_ZOOM_SYNC_COMMAND]);
    } catch (error) {
        logger.debug('Zoom sync listeners were already gone', error);
    }

    _handlers = null;

    for (const videoType of Object.keys(_sending)) {
        clearTimeout(_sending[videoType].timer);
        _sending[videoType] = {
            sent: undefined,
            timer: null
        };
    }
}

/**
 * Handles a zoom command from another participant's presence. Presence is
 * re-delivered whenever anything in it changes, so only real changes reach
 * redux; otherwise every unrelated presence update would snap the viewer's
 * large video back to the sender's framing.
 *
 * @param {Store} store - The redux store.
 * @param {string} videoType - 'camera' or 'desktop'.
 * @param {Object} node - The parsed presence node.
 * @param {string} id - The sender's participant id.
 * @private
 * @returns {void}
 */
function _onCommand(store, videoType, node, id) {
    const state = store.getState();

    if (!id || id === getLocalParticipant(state)?.id) {
        return;
    }

    const zoomState = parseZoomCommand(node?.attributes);

    if (!zoomState) {
        return;
    }

    if (!isSameZoomState(getRemoteVideoZoom(state, id, videoType), zoomState)) {
        store.dispatch(remoteVideoZoomChanged(id, videoType, zoomState));
    }
}

/**
 * Writes the local zoom of a video type into presence, at most once per
 * {@code ZOOM_SYNC_SEND_INTERVAL_MS} while it keeps changing. The latest
 * state always goes out when the interval elapses.
 *
 * @param {Store} store - The redux store.
 * @param {string} videoType - 'camera' or 'desktop'.
 * @private
 * @returns {void}
 */
function _scheduleSend(store, videoType) {
    const pending = _sending[videoType];

    if (!pending || pending.timer) {
        return;
    }

    _send(store, videoType);

    pending.timer = setTimeout(() => {
        pending.timer = null;

        if (!isSameZoomState(pending.sent, getLocalVideoZoom(store.getState(), videoType))) {
            _scheduleSend(store, videoType);
        }
    }, ZOOM_SYNC_SEND_INTERVAL_MS);
}

/**
 * Sends the current local zoom of a video type as a presence command.
 *
 * @param {Store} store - The redux store.
 * @param {string} videoType - 'camera' or 'desktop'.
 * @private
 * @returns {void}
 */
function _send(store, videoType) {
    const state = store.getState();
    const conference = getCurrentConference(state);
    const zoomState = getLocalVideoZoom(state, videoType) || IDENTITY_ZOOM_STATE;
    const pending = _sending[videoType];

    if (!conference || isSameZoomState(pending.sent, zoomState)) {
        return;
    }

    // Nothing was ever shared and the video is not zoomed: the others already
    // show it as-is, no presence update needed.
    if (pending.sent === undefined && isSameZoomState(IDENTITY_ZOOM_STATE, zoomState)) {
        pending.sent = zoomState;

        return;
    }

    try {
        conference.sendCommand(COMMAND_BY_VIDEO_TYPE[videoType], {
            attributes: {
                zoom: zoomState.zoom,
                cx: zoomState.cx,
                cy: zoomState.cy,
                ar: zoomState.ar
            }
        });
        pending.sent = zoomState;
    } catch (error) {
        logger.warn('Failed to send the video zoom', error);
    }
}

/**
 * Tells the others a local video is no longer zoomed.
 *
 * @param {Store} store - The redux store.
 * @param {string} videoType - 'camera' or 'desktop'.
 * @private
 * @returns {void}
 */
function _resetLocalZoom(store, videoType) {
    const current = getLocalVideoZoom(store.getState(), videoType);

    if (current && current.zoom > 1 + ZOOM_SYNC_EPSILON) {
        store.dispatch(localVideoZoomChanged(videoType, IDENTITY_ZOOM_STATE));
    }
}
